import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, EventBus, Events } from '../../managers';
import { Tooltip } from '../components/Tooltip';
import { rarityColor, sortAffixesForDisplay, buildItemTooltip } from '../components/TooltipContent';
import { formatAffixValue } from '../components/AffixDisplay';
import { formatStatName } from '../components/GameTerms';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { renderItemIcon, renderCurrencyIcon, renderDyeIcon } from '../components/ItemIcon';
import { renderStrandShowcase, bindStrandShowcase } from '../components/StrandShowcase';
import { ItemPicker } from '../components/ItemPicker';
import { RosterPicker, renderHomePickerCard } from '../components/RosterPicker';
import type {
  PlayerData,
  ItemData,
  ConfigData,
  AffixInstance,
  AffixDef,
  PartyUnit,
  UnitData,
} from '../../types';

type CraftTarget = { type: 'item'; data: ItemData };
type EquipSlot = 'weapon' | 'armor' | 'offhand' | 'accessory';

interface ParsedAffixes {
  implicit: { inst: AffixInstance; def: AffixDef | undefined } | null;
  explicits: { inst: AffixInstance; def: AffixDef | undefined; index: number }[];
  prefixCount: number;
  suffixCount: number;
}

const CRAFT_OPS = [
  { id: 'vividSpools',     op: 'reroll',         label: 'Reroll',       desc: 'Reroll all affixes',                 rarityReq: 'magic' as const },
  { id: 'prismaticSpools', op: 'reroll',         label: 'Reroll',       desc: 'Reroll all affixes',                 rarityReq: 'rare' as const },
  { id: 'bindings',        op: 'addAffix',       label: 'Add',          desc: 'Add a random affix',                 rarityReq: null },
  { id: 'shears',          op: 'removeAffix',    label: 'Remove',       desc: 'Remove a random affix',              rarityReq: null },
  { id: 'lustrousYarn',    op: 'upgradeRarity',  label: 'To Magic',     desc: 'Upgrade Normal → Magic',             rarityReq: 'normal' as const },
  { id: 'gildedYarn',      op: 'upgradeRarity',  label: 'To Rare',      desc: 'Upgrade Magic → Rare',               rarityReq: 'magic' as const },
  // Apply Dye is special — clicking opens a dye picker rather than spending
  // a fixed currency. The picker lists the player's dye stacks; the actual
  // spend happens when a dye is selected.
  { id: '__dyePicker__',   op: 'applyDye',       label: 'Apply Dye',    desc: 'Force a guaranteed affix',           rarityReq: null },
] as const;

/** Pretty per-tier label + accent color for dye picker UI. */
const DYE_TIER_INFO: Record<'pale' | 'deep' | 'saturated', { label: string; accent: string }> = {
  pale:      { label: 'Pale',      accent: '#94a3b8' },
  deep:      { label: 'Deep',      accent: '#475569' },
  saturated: { label: 'Saturated', accent: '#f8fafc' },
};

/** Per-dye theme metadata (color tint + per-slot mechanic copy). Kept
 *  in sync with src/config/dyes.ts on the backend. */
const DYE_THEMES: Record<string, { name: string; theme: string; hex: string; copy: { weapon: string; armor: string; offhand: string; accessory: string } }> = {
  crimson: { name: 'Crimson', theme: 'fire + blood',         hex: '#dc2626', copy: { weapon: 'Adds Fire damage',                              armor: 'Adds Maximum Life',                       offhand: 'Adds Lifesteal',                                  accessory: 'Adds Maximum Life' } },
  verdant: { name: 'Verdant', theme: 'nature + regen',       hex: '#16a34a', copy: { weapon: 'Adds Life On Hit',                              armor: 'Adds Life Regeneration',                  offhand: 'Adds Increased Healing',                          accessory: 'Adds Life Regeneration' } },
  azure:   { name: 'Azure',   theme: 'water + barrier',      hex: '#2563eb', copy: { weapon: 'Grants a Barrier On Hit',                       armor: 'DR while Barrier active',                 offhand: 'Resistance while Barrier active',                 accessory: 'Damage while Barrier active' } },
  citrine: { name: 'Citrine', theme: 'lightning + crit',     hex: '#eab308', copy: { weapon: 'Adds Increased Crit Chance',                   armor: 'Adds Lightning Resistance',               offhand: 'Adds Increased Crit Chance',                      accessory: 'Adds Increased Crit Chance' } },
  pearl:   { name: 'Pearl',   theme: 'wind + speed',         hex: '#f8fafc', copy: { weapon: 'Adds Speed',                                    armor: 'Adds Speed',                              offhand: 'Adds Speed',                                       accessory: 'Adds Speed' } },
  ochre:   { name: 'Ochre',   theme: 'earth + endurance',    hex: '#a16207', copy: { weapon: 'Adds Earth damage',                             armor: 'Adds Guard',                              offhand: 'Reduced DoT taken',                                accessory: 'Adds Increased Maximum Life' } },
  slate:   { name: 'Slate',   theme: 'steel + sharpness',    hex: '#64748b', copy: { weapon: 'Adds Steel Penetration',                        armor: 'Adds Steel Resistance',                   offhand: 'Adds First Strike bonus',                          accessory: 'Adds Steel Penetration' } },
  bronze:  { name: 'Bronze',  theme: 'brute + heft',         hex: '#b45309', copy: { weapon: 'Adds Brute damage',                             armor: 'Adds Increased Maximum Life',             offhand: 'Adds Impact damage',                              accessory: 'Adds Increased Maximum Life' } },
  silver:  { name: 'Silver',  theme: 'finesse + precision',  hex: '#cbd5e1', copy: { weapon: 'Adds Increased Crit Multi',                    armor: 'Adds Increased Crit Multi',               offhand: 'Adds Increased Crit Multi',                       accessory: 'Adds Increased Crit Multi' } },
  gilt:    { name: 'Gilt',    theme: 'holy + heal',          hex: '#fbbf24', copy: { weapon: 'Adds Holy damage',                              armor: 'Adds Increased Healing Received',         offhand: 'Adds Increased Healing Done',                     accessory: 'Adds Increased Healing Received' } },
  pitch:   { name: 'Pitch',   theme: 'dark + decay',         hex: '#1f2937', copy: { weapon: 'Adds DoT Potency',                              armor: 'Reduced DoT taken',                       offhand: 'Adds DoT Multiplier',                              accessory: 'Adds Status Duration' } },
  violet:  { name: 'Violet',  theme: 'astral + spell',       hex: '#9333ea', copy: { weapon: 'Adds Increased Astral damage',                 armor: 'Adds Astral Resistance',                  offhand: 'Adds Increased Spell damage',                      accessory: 'Adds Increased Maximum Focus' } },
  bone:    { name: 'Bone',    theme: 'necromancy + minion',  hex: '#e7e5d0', copy: { weapon: 'Adds Minion Damage',                            armor: 'Adds Minion Health',                      offhand: 'Adds Minion Damage',                              accessory: 'Adds Minion Speed' } },
};

/** Parse a dye storage id like `dye_crimson_pale` → (color, tier). */
function parseDyeStackId(id: string): { color: string; tier: 'pale' | 'deep' | 'saturated' } | null {
  const m = /^dye_([a-z]+)_(pale|deep|saturated)$/.exec(id);
  if (!m) return null;
  return { color: m[1], tier: m[2] as 'pale' | 'deep' | 'saturated' };
}

/** Wordsmith-blessed PoE-style mechanic labels for each affix the dye system can
 *  force. The chip below the row carries the value, so the label is the bare
 *  PoE descriptor with the modifier shape baked in (`Increased X` for percent,
 *  bare for flat, `Adds X Damage` for flat-damage rolls). When adding a new
 *  dye-eligible affix, add one line here. Missing entries fall back to the
 *  affix's `stat` field via formatStatName — uglier but never crashes. */
const DYE_AFFIX_LABELS: Record<string, string> = {
  // Crimson
  prefix_flat_fire_damage:      'Adds Fire Damage',
  prefix_hp_flat:               'Maximum Health',
  suffix_lifesteal:             'Lifesteal',
  // Verdant
  suffix_life_on_hit:           'Health On Hit',
  suffix_hp_regen:              'Health Regeneration',
  prefix_healing_done:          'Increased Healing',
  // Azure
  suffix_barrier_on_hit:        'Barrier On Hit',
  suffix_barrier_active_dr:     'Reduced Damage While Barrier Active',
  suffix_barrier_active_res:    'Resistance While Barrier Active',
  suffix_barrier_active_damage: 'Damage While Barrier Active',
  // Citrine
  suffix_increased_crit:        'Increased Critical Strike Chance',
  prefix_lightning_res:         'Lightning Resistance',
  // Pearl
  suffix_spd_flat:              'Speed',
  // Ochre
  prefix_flat_earth_damage:     'Adds Earth Damage',
  prefix_guard:                 'Guard',
  suffix_dot_damage_taken:      'Reduced Damage Over Time Taken',
  prefix_hp_percent:            'Increased Maximum Health',
  // Slate
  suffix_physical_pen:          'Physical Penetration',
  prefix_steel_res:             'Steel Resistance',
  suffix_first_strike:          'First Strike Damage',
  // Bronze
  prefix_flat_brute_damage:     'Adds Brute Damage',
  suffix_impact_damage:         'Fractured Damage',
  // Silver
  suffix_crit_multi:            'Critical Strike Multiplier',
  // Gilt
  prefix_flat_holy_damage:      'Adds Holy Damage',
  suffix_healing_received:      'Increased Healing Received',
  // Pitch
  suffix_dot_potency:           'Increased Damage Over Time',
  suffix_dot_multiplier:        'More Damage Over Time',
  suffix_debuff_duration:       'Increased Debuff Duration',
  suffix_buff_duration:         'Increased Buff Duration',
  suffix_debuff_extra_turn:     'Extra Turns to Debuffs',
  suffix_buff_extra_turn:       'Extra Turns to Buffs',
  // Violet
  prefix_tag_increased_astral:  'Increased Astral Damage',
  prefix_astral_res:            'Astral Resistance',
  prefix_tag_increased_spell:   'Increased Spell Damage',
  prefix_focus_percent:         'Increased Maximum Focus',
  // Bone
  prefix_minion_damage:         'Minion Damage',
  prefix_minion_health:         'Minion Maximum Health',
  suffix_minion_spd:            'Minion Speed',
};

const WORKSHOP_BG_URL = `${import.meta.env.BASE_URL}sprites/processed/hub_bg/the_workshop_v6.webp`;
const CHEST_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/props/workshop_chest.png`;
const LOOM_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/props/loom_prop.png`;

const SLOT_LABELS: Record<EquipSlot, string> = {
  weapon: 'Weapon',
  armor: 'Armor',
  offhand: 'Offhand',
  accessory: 'Accessory',
};

export class CraftingScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'crafting',
    name: 'Workshop',
    persistent: false,
    needsPhaser: false,
  };

  private selected: CraftTarget | null = null;
  private activeStrandId: string | null = null;
  private crafting = false;
  private craftMode: 'ops' | 'dye' = 'ops';

  constructor() {
    super({ rootId: 'crafting-screen-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'crafting-screen-root';
    root.innerHTML = `
      <div class="crafting-screen workshop-screen">
        ${screenHeaderHTML('Workshop')}
        <div class="ws-body">
          <!-- 16:9 cover-mode stage. The painted bg, chest, strand, and workbench
               all live inside the stage so they share ONE coordinate space and
               scale together as the window resizes. Mirrors home's menu-stage
               pattern. -->
          <div class="ws-stage">
            <div class="ws-bg" style="background-image:url('${WORKSHOP_BG_URL}')"></div>
            <div class="ws-vignette"></div>
            <!-- Chest prop — right-floor. Tap to open the item picker.
                 Diegetic "place item here" affordance. Replaces the loom. -->
            <div class="ws-chest" role="button" tabindex="0" aria-label="Place item in chest">
              <div class="ws-chest-glow"></div>
              <img class="ws-chest-sprite" src="${CHEST_SPRITE_URL}" alt="" draggable="false"
                   onerror="this.src='${LOOM_SPRITE_URL}'">
            </div>

            <!-- Strand zone — left side. Equip chips ABOVE the strand so the
                 sprite stays floor-anchored; otherwise the bottom-anchored
                 column lifts the strand off the floor when chips render. -->
            <div class="ws-strand-zone">
              <div class="ws-equip-chips"></div>
              <div class="ws-strand-slot"></div>
            </div>

            <!-- Workbench card — appears CENTER-LEFT of the chest only when an
                 item is loaded. Compact, wood-tone, NOT a giant centered modal.
                 Contains item details + crafting actions + equip button. -->
            <div class="ws-workbench hidden">
              <div class="ws-workbench-content"></div>
            </div>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    const chest = this.$('.ws-chest');
    if (chest) {
      chest.addEventListener('click', () => this.openItemPicker());
      chest.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          this.openItemPicker();
        }
      });
    }
  }

  protected setupSubscriptions(): void {
    this.subscribeState('playerData', () => {
      this.renderCurrencies();
      this.renderOps();
    });
    this.subscribeState('itemData', () => this.refreshSelected());
    this.subscribeState('unitData', () => {
      this.ensureActiveStrand();
      this.renderStrandSlot();
      this.renderEquipChips();
    });
    this.subscribeState('configData', () => this.onRender());
  }

  onEnter(): void {
    this.selected = null;
    this.crafting = false;
    this.craftMode = 'ops';
    this.ensureActiveStrand();
    Tooltip.hide();
    this.onRender();
  }

  onLeave(): void {
    Tooltip.hide();
    ItemPicker.close();
    RosterPicker.close();
  }

  protected onRender(): void {
    this.renderCurrencies();
    this.renderStrandSlot();
    this.renderEquipChips();
    this.renderLoomCard();
    this.renderOps();
  }

  // Default to first party member so the strand is ALWAYS visible
  private ensureActiveStrand(): void {
    if (this.activeStrandId) return;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const first = unitData?.party?.[0] ?? unitData?.roster?.[0];
    if (first) this.activeStrandId = first.id;
  }

  // ─── Currency Display ───

  private renderCurrencies(): void {
    if (!this.rootEl) return;
    renderScreenCurrencies(this.rootEl, 'workshop');
  }

  // ─── Strand Slot ───

  private renderStrandSlot(): void {
    const slot = this.$('.ws-strand-slot');
    if (!slot) return;

    const unit = this.getActiveStrand();
    if (!unit) {
      slot.innerHTML = '';
      return;
    }

    const configData = StateManager.get('configData') as ConfigData | undefined;
    slot.innerHTML = renderStrandShowcase({
      unit,
      configData,
      swappable: true,
      extraClasses: 'party-member--workshop',
      swapLabel: '⇄ Switch',
    });

    const memberEl = slot.querySelector<HTMLElement>('.party-member--tappable');
    if (memberEl) {
      bindStrandShowcase(memberEl, { onSwap: () => this.openStrandPicker() });
    }
  }

  // ─── Equipped-Item Chips (above the strand) ───
  // Chips are READ-ONLY context. They show what the active strand has on right
  // now. Crafting flows go through the loom; chips are not interactive picker
  // shortcuts. Tooltip-on-hover surfaces the affixes for inspection.

  private renderEquipChips(): void {
    const chipsEl = this.$('.ws-equip-chips');
    if (!chipsEl) return;

    const unit = this.getActiveStrand();
    if (!unit) {
      chipsEl.innerHTML = '';
      return;
    }

    const itemData = (StateManager.get('itemData') as ItemData[] | undefined) ?? [];
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const slots: { slot: EquipSlot; itemId: string | null }[] = [
      { slot: 'weapon',  itemId: unit.weaponId },
      { slot: 'armor',   itemId: unit.armorId },
      { slot: 'offhand', itemId: unit.offhandId },
      { slot: 'accessory', itemId: unit.accessoryId },
    ];

    const html = slots.map(({ slot, itemId }) => {
      const item = itemId ? itemData.find(i => i.id === itemId) : null;

      if (!item) {
        return `
          <div class="ws-chip ws-chip--empty" data-slot="${slot}">
            <span class="ws-chip-empty-label">${SLOT_LABELS[slot]}</span>
          </div>
        `;
      }
      const rColor = rarityColor(item.rarity ?? 'normal');
      const iconHtml = renderItemIcon(item.baseType, item.category, 'md');
      return `
        <div class="ws-chip ws-chip--filled" data-slot="${slot}" data-item-id="${item.id}" style="--rarity-color:${rColor}">
          ${iconHtml}
        </div>
      `;
    }).join('');

    chipsEl.innerHTML = html;

    // Filled chips are clickable: load that equipped item on the loom for
    // crafting (unequip-in-place becomes possible from the workbench's Equip
    // toggle once an item is loaded).
    chipsEl.querySelectorAll<HTMLElement>('.ws-chip--filled').forEach(el => {
      const id = el.dataset.itemId!;
      const item = itemData.find(i => i.id === id);
      if (!item) return;
      Tooltip.attach(el, () => buildItemTooltip(item, configData, unit));
      el.addEventListener('click', () => {
        Tooltip.hide();
        this.selectItem(item);
      });
    });

    // Empty chips open a slot-filtered ItemPicker; pick equips directly to the
    // active strand (no loom round-trip — fastest path to "fill an empty slot").
    chipsEl.querySelectorAll<HTMLElement>('.ws-chip--empty').forEach(el => {
      const slot = el.dataset.slot as EquipSlot;
      el.addEventListener('click', () => this.openEquipPickerForSlot(slot));
    });
  }

  private openEquipPickerForSlot(slot: EquipSlot): void {
    const unit = this.getActiveStrand();
    if (!unit) return;
    const allItems = (StateManager.get('itemData') as ItemData[] | undefined) ?? [];
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const eligible = allItems.filter(i => i.category === slot && i.equippedToUnitId !== unit.id);

    ItemPicker.open({
      title: `Equip ${SLOT_LABELS[slot]}`,
      items: eligible,
      activeStrand: unit,
      emptyMessage: `No ${SLOT_LABELS[slot].toLowerCase()} in your inventory.`,
      isItemUnequippable: (item) => this.unequippableReason(unit, item, configData),
      onPick: (item) => this.equipPickedItem(unit.id, item.id),
    });
  }

  /** Returns a short reason string if `unit` can't equip `item`, else null.
   *  Mirrors PartyScreen's mastery-req + handedness checks. The full requirement
   *  list is also visible in the item tooltip — this is just a card-level signal. */
  private unequippableReason(unit: PartyUnit, item: ItemData, configData: ConfigData | undefined): string | null {
    const baseDef = configData?.itemBases?.[item.baseType];
    if (!baseDef) return null;

    // Mastery requirements
    const reqs = baseDef.masteryRequirements;
    if (reqs && reqs.length > 0) {
      const ranks = unit.computedStats.masteryRanks;
      const missing = reqs.filter(r => (ranks[r.masteryId] ?? 0) < r.rank);
      if (missing.length > 0) {
        const parts = missing.map(r => {
          const name = configData?.masteries?.[r.masteryId]?.name ?? r.masteryId;
          return `${name} ${r.rank}`;
        });
        return `Requires ${parts.join(', ')}`;
      }
    }

    // Handedness: held offhand blocked by a 2H weapon already equipped on this unit
    if (baseDef.category === 'offhand' && baseDef.grip === 'held' && unit.weaponId) {
      const items = (StateManager.get('itemData') as ItemData[] | undefined) ?? [];
      const weapon = items.find(i => i.id === unit.weaponId);
      const weaponBase = weapon ? configData?.itemBases?.[weapon.baseType] : undefined;
      if (weaponBase?.category === 'weapon' && weaponBase.handedness === '2h') {
        return 'Requires a one-handed weapon';
      }
    }

    // Quivered offhand requires a bow equipped in the mainhand
    if (baseDef.category === 'offhand' && baseDef.grip === 'quivered') {
      const items = (StateManager.get('itemData') as ItemData[] | undefined) ?? [];
      const weapon = unit.weaponId ? items.find(i => i.id === unit.weaponId) : undefined;
      const weaponBase = weapon ? configData?.itemBases?.[weapon.baseType] : undefined;
      if (!weaponBase || weaponBase.category !== 'weapon' || weaponBase.family !== 'bow') {
        return 'Requires Bow equipped';
      }
    }

    return null;
  }

  private async equipPickedItem(unitId: string, itemId: string): Promise<void> {
    const result = await APIManager.equipItem(unitId, itemId);
    if (!result.success) {
      EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Equip failed' });
      return;
    }
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
        playerData: stateResult.data.playerData,
      });
    }
  }

  private getActiveStrand(): PartyUnit | null {
    if (!this.activeStrandId) return null;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    return unitData?.roster.find(u => u.id === this.activeStrandId) ?? null;
  }

  // ─── Workbench Card (item details + crafting ops) ───
  // Floating compact card anchored center-left of the chest. Hidden when no
  // item is loaded — the empty state is the painted stage with chest + strand.

  private renderLoomCard(): void {
    const panel = this.$('.ws-workbench');
    const content = this.$('.ws-workbench-content');
    if (!panel || !content) return;

    if (!this.selected) {
      panel.classList.add('hidden');
      content.innerHTML = '';
      return;
    }

    panel.classList.remove('hidden');

    const data = this.selected.data;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const rColor = rarityColor(data.rarity ?? 'normal');
    const parsed = this.parseAffixes(data);
    const slotLabel = data.category.charAt(0).toUpperCase() + data.category.slice(1);
    const rarityLabel = (data.rarity ?? 'normal').charAt(0).toUpperCase() + (data.rarity ?? 'normal').slice(1);

    let affixHTML = '';
    if (parsed.implicit) {
      const { inst, def } = parsed.implicit;
      affixHTML += `<div class="craft-affix-line craft-affix-implicit"><span class="craft-affix-value">${def ? this.formatAffix(inst, def, configData) : inst.affixId}</span></div>`;
    }
    if (parsed.implicit && parsed.explicits.length > 0) {
      affixHTML += `<div class="craft-affix-separator"></div>`;
    }
    const sortedInsts = sortAffixesForDisplay(parsed.explicits.map(e => e.inst), configData);
    for (const inst of sortedInsts) {
      const entry = parsed.explicits.find(e => e.inst === inst);
      if (!entry) continue;
      const typeClass = entry.def?.type === 'prefix' ? 'craft-affix-prefix' : 'craft-affix-suffix';
      affixHTML += `<div class="craft-affix-line ${typeClass}"><span class="craft-affix-value">${entry.def ? this.formatAffix(inst, entry.def, configData) : inst.affixId}</span></div>`;
    }
    if (parsed.explicits.length === 0 && data.rarity !== 'normal') {
      affixHTML += `<div class="craft-affix-empty">No explicit affixes</div>`;
    }

    const iconHtml = renderItemIcon(data.baseType, data.category, 'lg');
    const itemCardHTML = `
      <div class="craft-item-card">
        <div class="craft-item-header">
          <div class="craft-item-icon" style="border-color:${rColor}">${iconHtml}</div>
          <div class="craft-item-identity">
            <div class="craft-item-name" style="color:${rColor}">${data.name}</div>
            <div class="craft-item-subtitle">${rarityLabel} · ${slotLabel}</div>
          </div>
        </div>
        <div class="craft-affix-list">${affixHTML}</div>
        ${this.craftMode === 'ops' ? '<div class="ws-equip-section"></div>' : ''}
      </div>
    `;

    if (this.craftMode === 'dye') {
      content.innerHTML = itemCardHTML + `
        <div class="ws-workbench-section ws-workbench-section--dye">
          <div class="ws-dye-header">
            <button class="ws-dye-back" type="button" aria-label="Back to crafting">← Back</button>
            <div class="ws-dye-titles">
              <div class="ws-dye-title">Apply Dye</div>
              <div class="ws-dye-sub">${slotLabel} slot · always rolls Rare with one guaranteed affix</div>
            </div>
          </div>
          <div class="ws-dye-rows"></div>
        </div>
      `;
      this.$('.ws-dye-back')?.addEventListener('click', () => this.exitDyeMode());
      this.renderDyePicker();
    } else {
      content.innerHTML = itemCardHTML + `
        <div class="ws-workbench-section">
          <div class="ws-workbench-section-label">Crafting</div>
          <div class="craft-ops-grid"></div>
        </div>
      `;
      this.renderOps();
      this.renderEquipAction();
    }
  }

  // ─── Equip Action (separate card above crafting actions) ───

  private renderEquipAction(): void {
    const wrap = this.$('.ws-equip-section');
    if (!wrap || !this.selected) return;

    const item = this.selected.data;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    if (!unitData) { wrap.innerHTML = ''; return; }

    const activeStrand = this.getActiveStrand();
    const owner = item.equippedToUnitId
      ? unitData.roster.find(u => u.id === item.equippedToUnitId) ?? null
      : null;

    let action: 'equip' | 'unequip' | 'transfer' | null = null;
    let label = '';
    let desc = '';

    if (!owner && activeStrand) {
      action = 'equip';
      label = `Equip to ${activeStrand.name}`;
      desc = `Put on the active strand`;
    } else if (owner && activeStrand && owner.id === activeStrand.id) {
      action = 'unequip';
      label = 'Unequip';
      desc = `Move to inventory`;
    } else if (owner && activeStrand && owner.id !== activeStrand.id) {
      action = 'transfer';
      label = `Move to ${activeStrand.name}`;
      desc = `Transfer from ${owner.name}`;
    }

    if (!action) { wrap.innerHTML = ''; return; }

    wrap.innerHTML = `
      <button class="craft-op-equip" data-equip-action="${action}" type="button" title="${desc}">
        ${label}
      </button>
    `;

    wrap.querySelector<HTMLElement>('.craft-op-equip')?.addEventListener('click', () => {
      this.executeEquip(action!);
    });
  }

  // ─── Big Action Buttons (Crafting card grid) ───
  // Restored from the old layout — full label + description + cost icon, in a
  // 2-col grid. Only contextually-valid ops appear (no "Magic only" disabled
  // noise). If the player can't afford, the card greys out with the reason.

  private renderOps(): void {
    const opsEl = this.$('.craft-ops-grid');
    if (!opsEl) return;
    if (!this.selected) {
      opsEl.innerHTML = '';
      return;
    }

    const player = StateManager.get('playerData') as PlayerData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const data = this.selected.data;
    const parsed = this.parseAffixes(data);
    const { maxPrefixes, maxSuffixes } = this.getMaxSlots(data.rarity ?? 'normal');
    const hasRoom = (parsed.prefixCount < maxPrefixes) || (parsed.suffixCount < maxSuffixes);

    const validOps = CRAFT_OPS.filter(op => {
      if (op.op === 'reroll' && op.rarityReq && data.rarity !== op.rarityReq) return false;
      if (op.op === 'reroll' && data.rarity === 'normal') return false;
      if (op.op === 'addAffix' && data.rarity === 'normal') return false;
      if (op.op === 'removeAffix' && parsed.explicits.length === 0) return false;
      if (op.op === 'upgradeRarity' && op.rarityReq && data.rarity !== op.rarityReq) return false;
      // Apply Dye: any non-unique works (Normal and Magic both upgrade to Rare).
      if (op.op === 'applyDye' && data.rarity === 'unique') return false;
      return true;
    });

    if (validOps.length === 0) {
      opsEl.innerHTML = `<div class="craft-ops-empty">No crafting available for this item.</div>`;
      return;
    }

    const html = validOps.map(op => {
      // Apply Dye is special — no fixed currency, opens a picker. Show total
      // dye stack count as the "amount" indicator.
      if (op.op === 'applyDye') {
        const dyes = this.parsePlayerDyes(player);
        const totalCount = Object.values(dyes).reduce((s, n) => s + n, 0);
        const canAfford = totalCount >= 1;
        let disabled = !canAfford || this.crafting;
        const reason = !canAfford ? 'No dyes' : '';
        return `
          <button class="craft-op-btn${disabled ? ' disabled' : ''}" data-op="${op.op}" ${disabled ? 'disabled' : ''} title="${op.desc}">
            <span class="craft-op-label">${op.label}</span>
            <span class="craft-op-desc">${reason || op.desc}</span>
            <span class="craft-op-cost" style="color:#cbd5e1">🧪 ${totalCount}</span>
          </button>
        `;
      }

      const cur = configData?.currencies?.[op.id];
      const amount = player ? (player as unknown as Record<string, number>)[op.id] ?? 0 : 0;
      const canAfford = amount >= 1;

      let disabled = false;
      let reason = '';
      if (op.op === 'addAffix' && !hasRoom) { disabled = true; reason = 'Slots full'; }
      else if (!canAfford) { disabled = true; reason = 'Insufficient'; }
      if (this.crafting) disabled = true;

      const color = cur?.color ?? '#888';
      const iconImg = cur ? renderCurrencyIcon(cur.id, 20) : '?';
      const curName = cur?.name ?? op.id;

      return `
        <button class="craft-op-btn${disabled ? ' disabled' : ''}" data-op="${op.op}" ${disabled ? 'disabled' : ''} title="${curName}: ${op.desc}">
          <span class="craft-op-label">${op.label}</span>
          <span class="craft-op-desc">${reason || op.desc}</span>
          <span class="craft-op-cost" style="color:${color}">${iconImg} ${amount}</span>
        </button>
      `;
    }).join('');

    opsEl.innerHTML = html;

    opsEl.querySelectorAll<HTMLElement>('.craft-op-btn:not(.disabled)').forEach(btn => {
      btn.addEventListener('click', () => {
        const op = btn.dataset.op;
        if (op) this.executeCraft(op);
      });
    });
  }

  // ─── Equip Execution ───

  private async executeEquip(action: 'equip' | 'unequip' | 'transfer'): Promise<void> {
    if (!this.selected) return;
    const item = this.selected.data;
    const activeStrand = this.getActiveStrand();
    if (!activeStrand && action !== 'unequip') return;

    let result;
    if (action === 'unequip') {
      const owner = item.equippedToUnitId;
      if (!owner) return;
      result = await APIManager.unequipItem(owner, item.category as 'weapon' | 'armor' | 'accessory' | 'offhand');
    } else {
      // equip + transfer both use the same endpoint; backend handles slot conflicts
      result = await APIManager.equipItem(activeStrand!.id, item.id);
    }

    if (!result.success) {
      EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? `${action} failed` });
      return;
    }

    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
        playerData: stateResult.data.playerData,
      });
    }
  }

  // ─── Pickers ───

  private openItemPicker(): void {
    const allItems = (StateManager.get('itemData') as ItemData[] | undefined) ?? [];
    // Equipped items are visible on the strand chips already — picker shows
    // only items in inventory. Crafting an equipped item = unequip it first.
    const items = allItems.filter(i => !i.equippedToUnitId);
    if (items.length === 0) {
      EventBus.emit(Events.SHOW_TOAST, { type: 'info', message: 'No unequipped items to craft.' });
      return;
    }

    ItemPicker.open({
      title: 'Choose an Item',
      items,
      activeStrand: this.getActiveStrand(),
      selectedId: this.selected?.data.id,
      onPick: (item) => this.selectItem(item),
    });
  }

  private openStrandPicker(): void {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    if (!unitData) return;

    const eligible = unitData.roster;
    if (eligible.length === 0) return;

    const configData = StateManager.get('configData') as ConfigData | undefined;
    RosterPicker.open({
      title: 'Switch Strand',
      units: eligible,
      renderCard: (unit) => renderHomePickerCard(unit, configData),
      onPick: (unit) => this.selectStrand(unit),
    });
  }

  // ─── Selection ───

  private selectItem(item: ItemData): void {
    Tooltip.hide();
    this.selected = { type: 'item', data: item };
    if (item.equippedToUnitId) this.activeStrandId = item.equippedToUnitId;
    this.craftMode = 'ops';
    this.onRender();
    this.flashLoom();
  }

  private selectStrand(unit: PartyUnit): void {
    this.activeStrandId = unit.id;
    if (this.selected && this.selected.data.equippedToUnitId !== unit.id) {
      this.selected = null;
      this.craftMode = 'ops';
    }
    this.onRender();
  }

  private flashLoom(): void {
    // Pulse the item card on the right panel when an item is loaded
    const card = this.$('.craft-item-card');
    if (!card) return;
    card.classList.remove('craft-flash');
    void (card as HTMLElement).offsetWidth;
    card.classList.add('craft-flash');
  }

  // ─── Craft Execution ───

  private async executeCraft(op: string): Promise<void> {
    if (!this.selected || this.crafting) return;

    Tooltip.hide();
    this.crafting = true;
    this.renderOps();

    const targetType = this.selected.type;
    const targetId = this.selected.data.id;

    // Apply Dye: swap the workbench's bottom section to dye-picker mode. The
    // actual API call fires when the player taps a tier chip, not here.
    if (op === 'applyDye') {
      this.crafting = false;
      this.craftMode = 'dye';
      this.renderLoomCard();
      return;
    }

    let result;
    switch (op) {
      case 'reroll':
        result = await APIManager.craftReroll(targetType, targetId);
        break;
      case 'addAffix':
        result = await APIManager.craftAddAffix(targetType, targetId);
        break;
      case 'removeAffix':
        result = await APIManager.craftRemoveAffix(targetType, targetId);
        break;
      case 'upgradeRarity':
        result = await APIManager.craftUpgradeRarity(targetType, targetId);
        break;
      default:
        this.crafting = false;
        return;
    }

    if (!result.success) {
      EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? `${op} failed` });
      this.crafting = false;
      this.renderOps();
      return;
    }

    this.crafting = false;

    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }

    const card = this.$('.craft-item-card');
    if (card) {
      card.classList.add('craft-flash');
      setTimeout(() => card.classList.remove('craft-flash'), 600);
    }
  }

  // ─── Refresh selected after state update ───

  private refreshSelected(): void {
    if (!this.selected) {
      this.renderLoomCard();
      this.renderOps();
      this.renderEquipChips();
      return;
    }

    const id = this.selected.data.id;
    const items = StateManager.get('itemData') as ItemData[] | undefined;
    const updated = items?.find(i => i.id === id);
    if (updated) {
      this.selected = { type: 'item', data: updated };
      this.activeStrandId = updated.equippedToUnitId ?? this.activeStrandId;
    } else {
      this.selected = null;
    }

    this.renderLoomCard();
    this.renderOps();
    this.renderEquipChips();
    this.renderStrandSlot();
  }

  // ─── Affix Helpers ───

  private parseAffixes(data: ItemData): ParsedAffixes {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    let implicit: ParsedAffixes['implicit'] = null;
    const explicits: ParsedAffixes['explicits'] = [];
    let prefixCount = 0;
    let suffixCount = 0;

    try {
      const inst: AffixInstance = JSON.parse(data.implicitAffix);
      if (inst.affixId) {
        const def = configData?.affixes?.[inst.affixId];
        implicit = { inst, def };
      }
    } catch { /* no implicit */ }

    try {
      const exps: AffixInstance[] = JSON.parse(data.explicitAffixes);
      exps.forEach((inst, index) => {
        const def = configData?.affixes?.[inst.affixId];
        explicits.push({ inst, def, index });
        if (def?.type === 'prefix') prefixCount++;
        else if (def?.type === 'suffix') suffixCount++;
      });
    } catch { /* no explicits */ }

    return { implicit, explicits, prefixCount, suffixCount };
  }

  private formatAffix(inst: AffixInstance, def: AffixDef, configData: ConfigData | undefined): string {
    return formatAffixValue(inst, def, configData);
  }

  private getMaxSlots(rarity: string): { maxPrefixes: number; maxSuffixes: number } {
    switch (rarity) {
      case 'magic': return { maxPrefixes: 1, maxSuffixes: 1 };
      case 'rare':  return { maxPrefixes: 3, maxSuffixes: 3 };
      default:      return { maxPrefixes: 0, maxSuffixes: 0 };
    }
  }

  // ─── Dye picker ───

  /** Parse the player's `dyes` JSON column into a stack-id → count map. */
  private parsePlayerDyes(player: PlayerData | undefined): Record<string, number> {
    if (!player?.dyes) return {};
    try {
      const obj = JSON.parse(player.dyes);
      if (!obj || typeof obj !== 'object') return {};
      // Sanitize: drop zero/negative counts, drop non-numeric values.
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'number' && v > 0) out[k] = Math.floor(v);
      }
      return out;
    } catch {
      return {};
    }
  }

  /** Render the in-workbench dye picker — one row per owned color with three
   *  tier chips (Pale/Deep/Saturated). Each chip displays the value range it
   *  rolls for the currently-selected slot's affix, so players can compare
   *  tiers at a glance instead of reading "Adds Maximum Life" three times. */
  private renderDyePicker(): void {
    if (!this.selected) return;
    const rowsEl = this.$('.ws-dye-rows');
    if (!rowsEl) return;

    const player = StateManager.get('playerData') as PlayerData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const dyes = this.parsePlayerDyes(player);
    const slot = (this.selected.data.category ?? 'weapon') as EquipSlot;

    const COLOR_ORDER = [
      'crimson', 'bronze', 'ochre', 'citrine', 'gilt',
      'verdant', 'azure', 'pearl', 'silver', 'slate',
      'violet', 'pitch', 'bone',
    ];
    const TIERS = ['pale', 'deep', 'saturated'] as const;

    const ownedColors = new Set<string>();
    for (const id of Object.keys(dyes)) {
      const parsed = parseDyeStackId(id);
      if (parsed) ownedColors.add(parsed.color);
    }
    const colors = COLOR_ORDER.filter(c => ownedColors.has(c) && DYE_THEMES[c]);

    if (colors.length === 0) {
      rowsEl.innerHTML = `<div class="ws-dye-empty">No dyes yet — earn them from Mordant encounters.</div>`;
      return;
    }

    const html = colors.map(color => {
      const theme = DYE_THEMES[color];
      const dyeDef = configData?.dyes?.[color];
      const slotMapping = dyeDef?.slots?.[slot];
      const affixDef = slotMapping ? configData?.affixes?.[slotMapping.affixId] : undefined;
      // Wordsmith-blessed label, with a never-crashes fallback to the raw stat name.
      const affixLabel = (slotMapping && DYE_AFFIX_LABELS[slotMapping.affixId])
        || (affixDef ? formatStatName(affixDef.stat) : '');

      // P/S badge — PoE players read prefix vs suffix as different "slots" on the
      // item. Tiny letter chip with full-word tooltip for new players.
      const affixType = affixDef?.type;
      const pinHTML = affixType === 'prefix' || affixType === 'suffix'
        ? `<span class="ws-dye-affix-pin ws-dye-affix-pin--${affixType}" title="${affixType === 'prefix' ? 'Prefix' : 'Suffix'}">${affixType === 'prefix' ? 'P' : 'S'}</span>`
        : '';

      const tierChips = TIERS.map(tier => {
        const stackId = `dye_${color}_${tier}`;
        const count = dyes[stackId] ?? 0;
        const owned = count > 0;
        const tierLabel = DYE_TIER_INFO[tier].label;
        const idx = slotMapping
          ? (tier === 'pale' ? slotMapping.paleIdx : tier === 'deep' ? slotMapping.deepIdx : slotMapping.saturatedIdx)
          : null;
        const range = idx != null && affixDef ? this.formatDyeTierRange(affixDef, idx) : '';
        return `
          <button class="ws-dye-chip${owned ? '' : ' ws-dye-chip--empty'}"
                  data-color="${color}" data-tier="${tier}"
                  ${owned ? '' : 'disabled'} type="button">
            <span class="ws-dye-chip-tier">${tierLabel}</span>
            <span class="ws-dye-chip-range">${range || '—'}</span>
            <span class="ws-dye-chip-count">${owned ? `×${count}` : '—'}</span>
          </button>
        `;
      }).join('');

      return `
        <div class="ws-dye-row" style="--dye-hex:${theme.hex}">
          <div class="ws-dye-row-head">
            <div class="ws-dye-icon-wrap" aria-hidden="true">${renderDyeIcon(color, 40)}</div>
            <div class="ws-dye-row-info">
              <div class="ws-dye-row-name-line">
                <span class="ws-dye-row-name">${theme.name}</span>
                ${pinHTML}
              </div>
              <div class="ws-dye-row-effect">${affixLabel}</div>
            </div>
          </div>
          <div class="ws-dye-row-tiers">${tierChips}</div>
        </div>
      `;
    }).join('');

    rowsEl.innerHTML = html;

    rowsEl.querySelectorAll<HTMLElement>('.ws-dye-chip:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => {
        const color = btn.dataset.color;
        const tier = btn.dataset.tier;
        if (!color || !tier) return;
        this.executeApplyDye(color, tier);
      });
    });
  }

  /** Format the value range a dye tier rolls. Each dye-tier maps to one
   *  affix-tier index — variance comes only from the affix's own min/max. */
  private formatDyeTierRange(affixDef: AffixDef, tierIdx: number): string {
    const tiers = affixDef.tiers ?? [];
    if (tiers.length === 0) return '';
    const tier = tiers[Math.max(0, Math.min(tiers.length - 1, tierIdx))];
    if (!tier) return '';

    // Flat damage uses dual-roll (low/high). Show the high-roll range as the
    // headline number — it's what dictates the damage spike feel.
    if (affixDef.category === 'flat_damage') {
      return `${tier.minValue}–${tier.maxValue} dmg`;
    }

    // Descale tier values for affixes that use sub-integer precision storage.
    const scale = affixDef.decimals ? Math.pow(10, affixDef.decimals) : 1;
    const min = tier.minValue / scale;
    const max = tier.maxValue / scale;
    const sign = '+';
    const suffix = affixDef.modifier === 'percent' ? '%' : '';
    if (min === max) return `${sign}${min}${suffix}`;
    return `${sign}${min}–${max}${suffix}`;
  }

  private exitDyeMode(): void {
    this.craftMode = 'ops';
    this.renderLoomCard();
  }

  private async executeApplyDye(color: string, tier: string): Promise<void> {
    if (!this.selected || this.crafting) return;
    Tooltip.hide();
    this.crafting = true;
    // Disable every chip while the request is in flight so a double-tap doesn't
    // burn two dyes.
    this.$('.ws-dye-rows')?.querySelectorAll('button').forEach(b => {
      (b as HTMLButtonElement).disabled = true;
    });

    // Preserve the picker's scroll position — refreshSelected will rebuild
    // the workbench in-place after the apply, and we don't want the player's
    // scroll-down-to-find-a-dye to reset every time they apply one.
    const savedScroll = this.$('.ws-dye-rows')?.scrollTop ?? 0;

    const itemId = this.selected.data.id;
    const result = await APIManager.craftApplyDye(itemId, color, tier);

    if (!result.success) {
      EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Apply dye failed' });
      this.crafting = false;
      this.renderDyePicker();
      return;
    }
    this.crafting = false;
    // Stay in dye mode so the player can keep applying dyes back-to-back.
    // The item card above re-renders with the new affix; chip counts decrement.

    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }

    // Restore scroll after the rebuild from refreshSelected has settled.
    const rowsAfter = this.$('.ws-dye-rows');
    if (rowsAfter) rowsAfter.scrollTop = savedScroll;

    const card = this.$('.craft-item-card');
    if (card) {
      card.classList.add('craft-flash');
      setTimeout(() => card.classList.remove('craft-flash'), 600);
    }
  }
}
